import React from "react";
import { Card, Button } from "@blueprintjs/core";
import Flexbox from "flexbox-react";
import ManagerContainerIntroBox from "./ManagerContainerIntroBox";
import AssessmentQuestionList from "./AssessmentQuestionList";
import AssessmentReadOnlyList from "./AssessmentReadOnlyList";
import GoalRatingForm from "./GoalRatingForm";
import GoalReadOnlyList from "./GoalReadOnlyList";
import EmptyStateNotice from "./EmptyStateNotice";
import { FilledButton, OutlinedButton } from "../buttons-20250224T111359Z-001/buttons";
import ReadOnlyNotice from "./ReadOnlyNotice";
import AcknowledgementTabContent from "./AcknowledgementTabContent";

const ReviewSectionRenderer = props => {
  const {
    mode,
    selectedStep,
    canEditReviewStep,
    onEdit,
    reviewData,
    goalData,
    subGoalsByGoalId,
    openSubGoals,
    toggleSubGoals,
    acknowledgementData,
    onAcknowledge,
    answers,
    ratings,
    commentAnswers,
    goalRatings,
    selectedTimeframe,
    managerGlobalfinalRating,
    managerGlobalfinalRatingLabel,
    averageGoalRating,
    averageGoalRatingLabel,
    averageReviewRating,
    averageReviewRatingLabel,
    categoryWiseRatings,
    onAnswerChange,
    onRatingChange,
    onCommentChange,
    onGoalRatingChange,
    onFileUpload,
    onSaveDraft,
    onSubmit,
    selectedCheckIn
  } = props;


  /*     EMPTY STATE    */

  if (mode === "empty") {
    return (
      <EmptyStateNotice
        title="Self assessment not submitted yet"
        description="The employee has not submitted their self assessment form."
      />
    );
  }

  if (mode === "locked") {
    return (
      <EmptyStateNotice
        title="Waiting for previous steps"
        description="This step will be available once earlier steps are submitted."
      />
    );
  }
  if (mode === "acknowledgement") {
    return (
      <AcknowledgementTabContent
        acknowledgementData={acknowledgementData}
        onAcknowledge={onAcknowledge}
      />
    );
  }


  const formattedAnswers = Object.keys(
    answers || {}
  ).map(questionId => {

    const answerObj = answers[questionId];
    // FILE TYPE
    if (
      typeof answerObj === "object" &&
      answerObj !== null &&
      "answer_file" in answerObj
    ) {
      return {
        id: questionId,
        assessment_question_id: questionId,
        assessment_value: answerObj.answer || "",
        assessment_file: answerObj.answer_file || null,
        file_url: answerObj.file_url || ""
      };
    }
    // CHECKBOX TYPE
    if (
      typeof answerObj === "object" && answerObj !== null) {
      return {
        id: questionId,
        assessment_question_id: questionId,
        assessment_value: answerObj
      };
    }
    // NORMAL INPUT
    return {
      id: questionId,
      assessment_question_id: questionId,
      assessment_value: answerObj || ""
    };

  });

  const groupedQuestions = reviewData.map(cat => {
    const normalized = {
      category_id: cat?.category_id || "uncategorized",
      category_name: cat?.category_name || "",
      average_rating:
        cat?.average_category_wise_rating ??
        cat?.average_rating ??
        null,

      average_rating_label:
        cat?.average_category_wise_rating_label ??
        cat?.average_rating_label ??
        ""
    };

    return {
      ...normalized,

      questions: (cat.questions || []).map(q => {
        const questionType =
          q.question_type ||
          q.answer_type ||
          "";

        const hasOptions =
          questionType === "Checkbox" ||
          questionType === "Radio" ||
          questionType === "DropDown";

        return {
          ...q,
          id: q.id,
          question: q.question,

          assessment_question_id: q.id,

          assessment_value:
            (q.answer ?? q.assessment_value ?? "") === "-"
              ? ""
              : (q.answer ?? q.assessment_value ?? ""),

          answer_type: questionType,
          question_type: questionType,

          value: hasOptions ? q.value : {},

          rating:
            q.rating ??
            q.assessment_rating ??
            null,

          weightage:
            q.weightage ?? null,

          file_url:
            questionType === "Files"
              ? (q.answer || q.file_url || "")
              : ""
        };
      }),

      remark_questions: [
        ...(cat.remark_questions || []),
        ...(cat.comment_questions || [])
      ].map(cq => ({
        id:
          cq.id ||
          cq.remark_question_id,

        question:
          cq.question ||
          cq.remark_question ||
          cq.comment_question ||
          "",

        answer:
          cq.answer ||
          cq.category_comment_answer ||
          cq.comment_answer ||
          ""
      }))
    };
  });

  const isReadOnly =
    mode === "readOnly" || Number(selectedCheckIn?.current_state) === 0;
  const totalQuestions = groupedQuestions.reduce(
    (count, category) =>
      count +
      (category.questions?.length || 0) +
      (category.remark_questions?.length || 0),
    0
  );
  const hasGoals = goalData?.length > 0;

  const showSubmitButton = !isReadOnly && (totalQuestions > 0 || hasGoals);
  // const showSubmitButton = !isReadOnly;

  return (
    <Flexbox
      flexDirection="column"
      style={{ gap: 20 }}
    >

      {isReadOnly && canEditReviewStep(selectedStep) && (
        <Flexbox justifyContent="flex-end">
          <FilledButton
            text="Edit Review"
            onClick={() => onEdit(selectedStep)}
          />
        </Flexbox>
      )}



      <ManagerContainerIntroBox
        iconType="manager"
        text="Review Form"
        description={
          isReadOnly
            ? "Submitted review responses."
            : "Provide your evaluation of employee performance."
        }
        managerGlobalRating={managerGlobalfinalRating}
        managerGlobalRatingLabel={managerGlobalfinalRatingLabel}
        rating={averageReviewRating}
        ratingLabel={averageReviewRatingLabel}
        containerType="assessment"
        isRatingEnabledQuestions={
          selectedTimeframe?.is_assessment_question_rating
        }
      />
      {isReadOnly && <ReadOnlyNotice />}

      <Card className="assessment-question-container">
        {isReadOnly ? (
          <>
            <AssessmentReadOnlyList
              data={groupedQuestions}
              hideRatingAndWeightage={selectedStep?.step_type === 2 ? true : false}
              ratingLabels={selectedTimeframe?.rating_labels || []}
              weightage={selectedTimeframe?.is_weight_allow}
              isRatingEnabledQuestions={selectedTimeframe?.is_assessment_question_rating}
              accountId={selectedTimeframe?.account_id}
              isAnswerAllowedOnQuestion={
                selectedTimeframe?.is_answer_allow_on_question
              }
            />
          </>
        ) : (
          <AssessmentQuestionList
            questions={groupedQuestions}
            stepType={selectedStep?.step_type}
            answers={formattedAnswers}
            assessmentRatings={ratings}
            remarkAnswers={commentAnswers}
            selectedTimeframe={selectedTimeframe}
            onChange={(question, value) => onAnswerChange(question.id, value)}
            updateAnswer={(question, value) => onAnswerChange(question.id, value)}
            onFileUpload={onFileUpload}
            onCheckboxChange={(
              question,
              optionData,
              checked
            ) => {
              const existing = answers[question.id] || {};
              let updated = { ...existing };
              if (checked) {
                updated[optionData.key] =
                  optionData.value;
              } else {
                delete updated[optionData.key];
              }
              onAnswerChange(question.id, updated);
            }}
            categoryWiseRatings={categoryWiseRatings}
            onRatingChange={(questionId, value) => onRatingChange(questionId, value)}
            updateRemarkAnswer={(remarkId, value) => onCommentChange(remarkId, value)}
            weightage={selectedTimeframe?.is_weight_allow}
            isRatingEnabledQuestions={selectedTimeframe?.is_assessment_question_rating}
          />
        )}
      </Card>


      {goalData?.length > 0 && selectedStep?.step_type !== 2 && (
        <>
          <ManagerContainerIntroBox
            iconType="manager"
            text="Goal Review"
            description={
              isReadOnly
                ? "Submitted goal ratings."
                : "Provide ratings for goals."
            }
            rating={averageGoalRating}
            ratingLabel={
              averageGoalRatingLabel
            }
            containerType="goal"
            isRatingEnabledQuestions={
              selectedTimeframe?.is_assessment_question_rating
            }
          />

          <Card className="assessment-question-container">
            {isReadOnly ? (
              <GoalReadOnlyList
                data={goalData}
                subGoalsByGoalId={subGoalsByGoalId}
                openSubGoals={openSubGoals}
                toggleSubGoals={toggleSubGoals}
                ratingLabels={selectedTimeframe?.rating_labels || []}
                weightage={selectedTimeframe?.is_weight_allow}
              />
            ) : (
              <GoalRatingForm
                goalList={goalData}
                goalRatings={goalRatings}
                subGoalsByGoalId={subGoalsByGoalId}
                openSubGoals={openSubGoals}
                toggleSubGoals={toggleSubGoals}
                onRatingChange={(goalId, value) =>
                  onGoalRatingChange(
                    goalId,
                    value === "" ? "" : Number(value)
                  )
                }
                weightage={selectedTimeframe?.is_weight_allow}
                ratingLabels={selectedTimeframe?.rating_labels || []}
                startRatingValue={selectedTimeframe?.start_rating_value}
                endRatingValue={selectedTimeframe?.end_rating_value}
              />
            )}
          </Card>
        </>
      )}

      {/* ------------------------------------------------------------------- */}
      {/*                             ACTIONS                                  */}
      {/* ------------------------------------------------------------------- */}

      {showSubmitButton && (
        <Flexbox
          justifyContent="flex-start"
          style={{ gap: 10 }}
        >
          <FilledButton
            text="Save Draft"
            onClick={onSaveDraft}
          />
          <OutlinedButton
            text="Save as Published"
            onClick={onSubmit}
          />
        </Flexbox>
      )}
    </Flexbox>
  );
};

export default ReviewSectionRenderer;